/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * TextBlockModal component for opening modal on child click
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.14.0
 * @since 1.10.0
 */

import { ReactElement, useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import Modal from '../../../modules/modal/components/Modal';
import { modalOpen } from '../../../modules/modal/modalActions';
import { IModalOpenPayload } from '../../../modules/modal/types/modalActionTypes';
import { TextBlockModalStyled } from './styled/TextBlockModalStyled';

interface ITextBlockModalProps {
    children: ReactElement;
    disabled?: boolean;
    text: string;
    title?: string;
}

type TTextBlockModal = ITextBlockModalProps & IDispatchProps;

const TextBlockModal = (props: TTextBlockModal) => {

    const {
        openModal,
        disabled,
        children,
        text,
        title,
    } = props;

    const openModalHandler = useCallback(() => {
        if (disabled) {
            return;
        }
        const printableText = text.replace(/(?:\r\n|\r|\n)/g, '<br />');
        openModal({
            render: () => <Modal
                dataName="text-block-modal-handler"
                width="900px"
            >
                {
                    title &&
                    <Modal.Title dataName="modal-popup-title">
                        {title}
                    </Modal.Title>
                }
                <Modal.Body>
                    <Modal.Text dataName="modal-popup-text">
                        <div dangerouslySetInnerHTML={({ __html: printableText })}/>
                    </Modal.Text>
                </Modal.Body>
            </Modal>,
        });
    }, [openModal, disabled, text, title]);

    return <TextBlockModalStyled onClick={openModalHandler}> {children} </TextBlockModalStyled>;
};

interface IDispatchProps {
    openModal: (payload: IModalOpenPayload) => void;
}

export const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
});

export default connect(null, mapDispatchProps)(TextBlockModal);
